import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { Alert, Autocomplete, Box, Button, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Link, useLocation } from 'react-router-dom';
import { tenantConfig } from 'config';
import { ConfigContext } from 'components/ConfigGuard';
import claimWalletCardIcon from 'assets/icons/claimWalletCard.svg';
import replaceWalletCardIcon from 'assets/icons/replaceWalletCard.svg';
import processingWalletCardIcon from 'assets/icons/processingWalletCard.svg';
import cardpos from 'assets/icons/card-pos.svg';
import { UserContext } from 'components/UserGuard';
import Modal from 'components/shared/Modal';
import CredentialService from 'services/api/CredentialService';
import UserService from 'services/api/UserService';
import routes from 'store/configs/Routes';
import Address from 'store/types/Address';
import { CurrentUser } from 'store/types/User';
import { defaultFormProps, isAddressInternational, willAddressShip } from 'util/Form';
import { isDefined } from 'util/Filter';
import { WALLET_CARD_POPUP_HIDDEN_LABEL, getExpDate } from 'util/LocalStorage';
import { errorMessage } from 'util/Request';
import { Completions } from 'store/types/Completions';

export function WalletCardButton() {
  const { pathname } = useLocation();
  const user = useContext(UserContext);
  const [creds, setCreds] = useState<Completions>();
  const pageLoad = useCallback(async () => {
    try {
      setLoading(true);
      const response = await CredentialService.getCompletions(user.nccerCardNumber ?? '', 'true', false, 'all');
      setCreds(response);

      const { isWalletCardReturned } = walletCardStatusCalculation(user.walletCardStatus);
      if (isWalletCardReturned && user.nccerCardNumber && pathname === routes.home && validProgramOrUSAdult(user, response)) {
        const sessionKey = WALLET_CARD_POPUP_HIDDEN_LABEL + 'returned_' + user.nccerCardNumber;
        if (!sessionStorage.getItem(sessionKey)) {
          sessionStorage.setItem(sessionKey, '1');
          setOpen(true);
        }
      }
    } catch (error) {
      setCreds(undefined);
    } finally {
      setLoading(false);
    }
  }, [user, pathname]);

  const validProgramOrUSAAdult = validProgramOrUSAdult(user, creds)
    
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(() => {
    const { isWalletCardClaimed, isWalletCardBatched, isWalletCardReturned } = walletCardStatusCalculation(user.walletCardStatus);

    if (isWalletCardReturned) return false;

    return (
      user.canClaimWalletCard &&
      validProgramOrUSAAdult &&
      !isWalletCardClaimed &&
      !isWalletCardBatched &&
      isDefined(user.birthDate) &&
      pathname === routes.home &&
      !isSuppressedWalletCardPopup(user.nccerCardNumber)
    );
  });
  const [workflow, setWorkflow] = useState(() => ({ step: 0, steps: getWorkflow(user) }));

  const current = useMemo(() => workflow.steps[workflow.step], [workflow.step, workflow.steps]);

  const reset = useCallback(() => {
    setWorkflow({ step: 0, steps: getWorkflow(user) });
  }, [user]);

  const openModal = useCallback(() => {
    reset();
    setOpen(true);
  }, [reset]);

  const closeModal = useCallback(() => setOpen(false), []);
  
  const dismissModal = useCallback((doNotDisplay = false) => {
    suppressWalletCardPopup(user.nccerCardNumber, doNotDisplay);
    closeModal();
  }, [closeModal, user.nccerCardNumber]);

  const moveNext = useCallback(() => {
    setWorkflow((e) => ({
      step: Math.max(0, Math.min(e.step + 1, e.steps.length - 1)),
      steps: e.steps,
    }));
  }, []);

  const moveTo = useCallback((step: number) => {
    setWorkflow((e) => ({
      step: Math.max(0, Math.min(step, e.steps.length - 1)),
      steps: e.steps,
    }));
  }, []);

  useEffect(() => {
      pageLoad();
    }, []);
  if (!workflow.steps.length) {
    return null;
  }

  return (
    <WalletCardContext.Provider value={{ setLoading, closeModal, dismissModal, moveNext, moveTo }}>
      {validProgramOrUSAAdult && <Button
        variant="outlined"
        color="secondary"
        disabled={loading}
        onClick={openModal}
        sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}
      >
        <img src={cardpos} />
        <Box display={{ xs: 'inline', sm: 'none', md: 'inline' }}>Claim Wallet Card</Box>
      </Button>}
      <Modal loading={loading} open={open} title={current.title} subTitleText={current.subtitle} onClose={closeModal} headerBodyDivider={current.headerBodyDivider}>
        {current.component}
      </Modal>
    </WalletCardContext.Provider>
  );
}

/**
 * Check the learner's programs are active or they are in the usa
 *
 * @returns Boolean
 *
 */
function validProgramOrUSAdult(user: CurrentUser, creds: Completions | undefined) {

  const mostRecentCompletion = creds?.Completions.filter((c) => c.ProgramStatus).sort((a, b) => {
    return (new Date(b.SubmissionDate).getTime() - new Date(a.SubmissionDate).getTime())
  })[0];

  if(creds && isDefined(user.birthDate) && !user.applyFerpaAgeRestriction && !isAddressInternational(user.address)){
    return true
  }
  else if (creds && (user.applyFerpaAgeRestriction || isAddressInternational(user.address) ) && mostRecentCompletion && mostRecentCompletion.ProgramStatus == "Active"){
    return true
  }
  return false
}

/**
 * Get the specific Claim Wallet Card workflow according to specific user criteria.
 *
 * @returns Array of workflow steps.
 *
 * @remarks
 * Unclaimed wallet card (default)
 *   Claim Card -> Address Confirmation (claim) -> Success
 * Unclaimed wallet card (age restricted / international)
 *   Batch card (batch) -> Success
 *
 * Claimed wallet card (default)
 *   Replace -> Address Confirmation (order replacement)
 * Claimed wallet card (age restricted / international)
 *   Batch replace (batch) -> Success
 *
 * Batched wallet card (age restricted / international)
 *   Success
 */
function getWorkflow({
  address,
  applyFerpaAgeRestriction,
  birthDate,
  canClaimWalletCard,
  walletCardStatus,
}: CurrentUser) {
  // Wallet card cannot be claimed.
  if (!canClaimWalletCard) {
    return [];
  }

  // Date of birth is not defined.
  if (!isDefined(birthDate)) {
    return [
      {
        title: 'Claim Wallet Card',
        subtitle: '',
        component: <EmptyDateOfBirth />,
      },
    ];
  }

  const { isWalletCardClaimed, isWalletCardBatched, isWalletCardReturned } = walletCardStatusCalculation(walletCardStatus);

  /// Wallet card has been batched but not processed yet.
  if (isWalletCardBatched) {
    return [
      {
        title: 'Claim Card through Organization',
        subtitle: batchSubtitle(address, applyFerpaAgeRestriction),
        component: <Success />,
      },
    ];
  }

  /// Wallet card was returned due to a delivery issue (domestic).
  if (isWalletCardReturned) {
    return [
      {
        title: 'Issue Delivering Your Wallet Card',
        subtitle: "USPS couldn't deliver your wallet card due to an issue with the mailing address provided. Please update your address below so we can ship your card to the right destination.",
        component: <ReturnedWalletCardForm />,
      },
      {
        title: 'Your Card Won\'t Be Sent',
        subtitle: 'We can\'t resend your wallet card until your mailing address is updated.',
        component: <ReturnedSkipWarning />,
        headerBodyDivider: true,
      },
      {
        title: 'Your Card Is On Its Way',
        subtitle: 'Hang tight - we are working on printing your wallet card and it should be at your door step soon.',
        component: <Success />,
      },
    ];
  }

  /// Wallet card has already been claimed.
  if (isWalletCardClaimed) {
    // Age restricted or international user (do not replace card).
    if (applyFerpaAgeRestriction || isAddressInternational(address)) {
      return [
        {
          title: 'Replace Card',
          subtitle: 'Looks like you have already claimed your wallet card and it has been mailed out. If you haven’t received it, check with the NCCER instructor or program manager you most recently completed training under. Note that it may take up to two weeks to receive.',
          component: <IssuedWalletCard />,
        }
      ];
    }

    // Default user (replace card -> order replacement).
    return [
      {
        title: 'Replace Card',
        subtitle:
          'Looks like you have already claimed your free wallet card. Click below to purchase a replacement card.',
        component: <WalletCardIntro icon={replaceWalletCardIcon} />,
      },
      {
        title: 'Address confirmation',
        subtitle: 'Please confirm your Date of Birth and Address.',
        component: <ReplaceWalletCardAddressConfirmation address={address} birthDate={birthDate} />,
      },
    ];
  }

  /// Wallet card is neither claimed nor batched.
  // Age restricted or international user (batch card).
  if (applyFerpaAgeRestriction || isAddressInternational(address)) {
    const title = applyFerpaAgeRestriction? 'Date of Birth confirmation': 'Address and Date of Birth confirmation'
    const confirm = applyFerpaAgeRestriction? 'Please confirm your Date of Birth.': 'Please confirm your Date of Birth and Address.'
    return [
      {
        title: 'Claim Wallet Card',
        subtitle:
          'Good News! Your first wallet card is FREE and ready to be claimed. It will be sent to your organization or school.',
          component: <WalletCardIntro icon={claimWalletCardIcon} />,
      },
      {
        title: title,
        subtitle: confirm,
        component: <ClaimWalletCardAddressConfirmation address={address} birthDate={birthDate} applyFerpaAgeRestriction={applyFerpaAgeRestriction}/>,
      },
      {
        title: 'Claim Card through Organization',
        subtitle: batchSubtitle(address, applyFerpaAgeRestriction),
        component: <Success />,
      },
    ];
  }

  // Default user (claim card).
  return [
    {
      title: 'Claim Wallet Card',
      subtitle:
        'Good News! Your first wallet card is FREE and ready to be claimed. It will be sent to the address in your account profile.',
      component: <WalletCardIntro icon={claimWalletCardIcon} />,
    },
    {
      title: 'Address confirmation',
      subtitle: 'Please confirm your Date of Birth and Address.',
      component: <ClaimWalletCardAddressConfirmation address={address} birthDate={birthDate}/>,
    },
    {
      title: 'Your Card Is On Its Way',
      subtitle: 'Hang tight - we are working on printing your wallet card and it should be at your door step soon.',
      component: <Success />,
    },
  ];
}

interface WalletCardIntroProps {
  icon: string;
}

function WalletCardIntro({ icon }: WalletCardIntroProps) {
  const { dismissModal, moveNext } = useContext(WalletCardContext);
  const [doNotDisplay, setDoNotDisplay] = useState(false);

  return (
    <Box display="flex" flexDirection="column" gap={2} alignItems="center">
      <img src={icon} />

      <Box display="flex" flexDirection="column" gap={2} width={1} pt={4}>
        <Button color="primary" variant="contained" onClick={moveNext}>
          Continue
        </Button>
        <Button color="secondary" variant="outlined" onClick={(e) => {dismissModal(doNotDisplay)}}>
          No Thanks
        </Button>
        <FormControlLabel
            control={<Checkbox 
              onChange={(e) => setDoNotDisplay(e.target.checked)}
            />}
            label="Do not show again."
          />
      </Box>
    </Box>
  );
}

function ClaimWalletCardAddressConfirmation({ address, birthDate, applyFerpaAgeRestriction = false }: AddressProps) {
  const claimWalletCard = useClaimWalletCard();

  return <AddressConfirmation address={address} birthDate={birthDate} applyFerpaAgeRestriction={applyFerpaAgeRestriction} submitText="Claim My Card" onSubmit={claimWalletCard} />;
}

function ReplaceWalletCardAddressConfirmation({ address, birthDate }: AddressProps) {
  const { closeModal } = useContext(WalletCardContext);

  const replaceWalletCard = useCallback(() => {
    // Route to nopCommerce.
    window.open(tenantConfig.replaceWalletCardUrl, '_blank')?.focus();
    closeModal();
  }, [closeModal]);

  return <AddressConfirmation address={address} birthDate={birthDate} submitText="Replace Card" onSubmit={replaceWalletCard} />;
}

function IssuedWalletCard() {
  const { dismissModal } = useContext(WalletCardContext);

  return (
    <Box display="flex" flexDirection="column" gap={2} alignItems="center">

      <Button color="secondary" variant="outlined" fullWidth onClick={dismissModal}>
        Close
      </Button>
    </Box>
  );
}

interface SmartySuggestion {
  street_line: string;
  city: string;
  state: string;
  zipcode: string;
}

function ReturnedWalletCardForm() {
  const { moveTo } = useContext(WalletCardContext);
  const { smartyEmbeddedKey } = useContext(ConfigContext);
  const { enqueueSnackbar } = useSnackbar();
  const submit = useReturnedWalletCardSubmit();
  const [suggestions, setSuggestions] = useState<SmartySuggestion[]>([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState<SmartySuggestion | null>(null);
  const debounceTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortController = React.useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      if (abortController.current) abortController.current.abort();
    };
  }, []);

  const fetchSuggestions = useCallback((query: string) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (query.length < 3) { setSuggestions([]); return; }
    if (!smartyEmbeddedKey) { setSuggestions([]); return; }
    debounceTimer.current = setTimeout(async () => {
      if (abortController.current) abortController.current.abort();
      abortController.current = new AbortController();
      try {
        const res = await fetch(
          `https://us-autocomplete-pro.api.smarty.com/lookup?key=${smartyEmbeddedKey}&search=${encodeURIComponent(query)}&max_results=5`,
          { signal: abortController.current.signal }
        );
        const data = await res.json();
        setSuggestions(data.suggestions ?? []);
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        setSuggestions([]);
        enqueueSnackbar('Unable to load address suggestions. Please try again.', { variant: 'error' });
      }
    }, 500);
  }, [enqueueSnackbar, smartyEmbeddedKey]);

  const form = useForm({
    ...defaultFormProps,
    defaultValues: { street1: '', street2: '' },
  });
  const { control, handleSubmit } = form;

  return (
    <FormProvider {...form}>
      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography variant="h5">Address</Typography>
          <Controller
            name="street1"
            control={control}
            rules={{ required: 'Address is required', validate: () => !!selectedSuggestion || 'Please select an address from the suggestions' }}
            render={({ field, fieldState }) => (
              <Autocomplete
                freeSolo
                filterOptions={(x) => x}
                options={suggestions}
                getOptionLabel={(o) => typeof o === 'string' ? o : `${o.street_line}, ${o.city}, ${o.state} ${o.zipcode}`}
                inputValue={field.value}
                onInputChange={(_, value, reason) => {
                  field.onChange(value);
                  if (reason === 'input') {
                    setSelectedSuggestion(null);
                    fetchSuggestions(value);
                  }
                }}
                onChange={(_, value) => {
                  if (value && typeof value !== 'string') {
                    field.onChange(value.street_line);
                    setSelectedSuggestion(value);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder="Start typing your address..."
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            )}
          />
        </Box>
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography variant="h5">Apt, Unit, Suite, etc (Optional)</Typography>
          <Controller
            name="street2"
            control={control}
            render={({ field }) => <TextField {...field} fullWidth />}
          />
        </Box>
        <Box display="flex" flexDirection="column" gap={2} mt={2}>
          <Button
            color="primary"
            variant="contained"
            fullWidth
            disabled={!selectedSuggestion}
            onClick={handleSubmit(({ street1, street2 }) => {
              if (debounceTimer.current) clearTimeout(debounceTimer.current);
              if (abortController.current) abortController.current.abort();
              submit(street1, street2, selectedSuggestion!);
            })}
          >
            Save Address & Ship Card
          </Button>
          <Button color="secondary" variant="outlined" fullWidth onClick={() => moveTo(1)}>
            Not Now
          </Button>
        </Box>
      </Box>
    </FormProvider>
  );
}

function ReturnedSkipWarning() {
  const { closeModal, moveTo } = useContext(WalletCardContext);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Alert severity="error">
        If you skip this step, your card will remain undelivered. You can still claim your card and update your address by selecting 'Claim Wallet Card' any time.
      </Alert>
      <Button color="secondary" variant="outlined" fullWidth onClick={closeModal}>
        Skip for Now
      </Button>
      <Button color="primary" variant="contained" fullWidth onClick={() => moveTo(0)}>
        Go Back and Update
      </Button>
    </Box>
  );
}

interface AddressProps {
  address: Address;
  birthDate: Date;
  applyFerpaAgeRestriction?: boolean;
}

interface AddressConfirmationProps<T> extends AddressProps {
  submitText: string;
  onSubmit: () => T | Promise<T>;
}

function AddressConfirmation<T>({ address, birthDate, submitText, onSubmit, applyFerpaAgeRestriction = false }: AddressConfirmationProps<T>) {
  const { closeModal } = useContext(WalletCardContext);

  const form = useForm({
    ...defaultFormProps,
    defaultValues: { addressAcknowledged: false , birthdateAcknowledged: false},
  });
  const { formState, handleSubmit } = form;

  const validAddress = willAddressShip(address);
  const submitDisabled = (!formState.isValid && validAddress) || (applyFerpaAgeRestriction && !formState.isValid);
  const validAdult = validAddress && !applyFerpaAgeRestriction 

  return (
    <FormProvider {...form}>
      <Box display="flex" flexDirection="column" gap={2}>
      <Typography variant="h5" pb={2}>
          Date of Birth
          {birthDate ? (
              <Typography pt={1}>{birthDate.toDateString()}</Typography>
          ) : (
            <Typography pt={1}>No birthDate on file</Typography>
          )}
        </Typography>
        {!applyFerpaAgeRestriction && <Typography variant="h5" pb={2}>
          Address
          {address ? (
            <>
              <Typography pt={1}>{address.street1}</Typography>
              <Typography>
                {address.city} {address.stateRegion} {address.postalCode}
              </Typography>
              <Typography>{address.country}</Typography>
            </>
          ) : (
            <Typography pt={1}>No address on file</Typography>
          )}
        </Typography>}
        {applyFerpaAgeRestriction && 
          <>
            <Controller
              render={({ field }) => (
                <FormControlLabel
                  {...field}
                  inputRef={field.ref}
                  control={<Checkbox />}
                  label="I acknowledge that the above date of birth is correct for my wallet card."
                />
              )}
              name="birthdateAcknowledged"
              rules={{ required: true }}
            />
            <Typography>Click 'Settings' to edit your date of birth.</Typography>
          </>
        }
        {validAdult && (
          <>
            <Controller
              render={({ field }) => (
                  <FormControlLabel
                    {...field}
                    inputRef={field.ref}
                    control={<Checkbox />}
                    label="I acknowledge that the above date of birth and address are correct for shipping my wallet card."
                  />
              )}
              name="addressAcknowledged"
              rules={{ required: true }}
            />
            <Typography>Click 'Settings' to edit your date of birth or address.</Typography>
          </>
        )} 
        {!validAddress && !applyFerpaAgeRestriction &&
          <Typography>
            The address on your account profile is incomplete. Click 'Settings' to edit your address.
          </Typography>
        }

        <Button
          color="secondary"
          variant="outlined"
          fullWidth
          component={Link}
          to={routes.settings}
          onClick={closeModal}
          sx={{ mt: 2 }}
        >
          Settings
        </Button>
        {!(!validAddress && !applyFerpaAgeRestriction) &&
          <Button
            color="primary"
            variant="contained"
            disabled={submitDisabled}
            fullWidth
            onClick={handleSubmit(onSubmit)}
          >
            {submitText}
          </Button>
        }
      </Box>
    </FormProvider>
  );
}

function EmptyDateOfBirth() {
  const { closeModal } = useContext(WalletCardContext);

  return (
    <Box display="flex" flexDirection="column" gap={2} alignItems="center">
      <img src={claimWalletCardIcon} />

      <Typography pt={2}>
        Looks like there is no date of birth on your account profile. Click 'Settings' to edit your profile and enter
        your date of birth.
      </Typography>

      <Button
        color="secondary"
        variant="outlined"
        fullWidth
        component={Link}
        to={routes.settings}
        onClick={closeModal}
        sx={{ mt: 2 }}
      >
        Settings
      </Button>
    </Box>
  );
}

function Success() {
  const { closeModal } = useContext(WalletCardContext);

  return (
    <Box display="flex" flexDirection="column" gap={2} alignItems="center">
      <img src={processingWalletCardIcon} />

      <Button color="secondary" variant="outlined" fullWidth sx={{ mt: 2 }} onClick={closeModal}>
        Close
      </Button>
    </Box>
  );
}

function batchSubtitle(address: Address | undefined, applyFerpaAgeRestriction: boolean) {
  if (isAddressInternational(address)) {
    return 'Wallet cards for individuals with international mailing addresses will be sent to their NCCER training program.\n\nNote that it may take up to 1-2 months for your card to arrive depending on demands and the time until the next production cycle takes place.';
  } else if (applyFerpaAgeRestriction) {
    return 'In accordance with privacy regulations, NCCER cannot collect or store home addresses for individuals under the age of 18. Your wallet card will be sent to the program you\'ve most-recently taken NCCER training under. Note that it may take up to 1-2 months for your card to arrive depending on demands and the time until the next production cycle takes place.';
  } else {
    return 'Your wallet card is being mailed to your organization. Please claim it from them.';
  }
}

// Suppress automatic wallet card popup on page load.
function suppressWalletCardPopup(nccerCardNumber: string | undefined, doNotDisplay: boolean): void {
  const key = WALLET_CARD_POPUP_HIDDEN_LABEL + nccerCardNumber;
  // Suppress popup for a day.
  const timestamp = doNotDisplay? getExpDate(365) : getExpDate(1);
  localStorage.setItem(key, String(timestamp));
}

function isSuppressedWalletCardPopup(nccerCardNumber: string | undefined): boolean {
  const key = WALLET_CARD_POPUP_HIDDEN_LABEL + nccerCardNumber;
  const value = localStorage.getItem(key);
  const expiry = value ? parseFloat(value) : 0;

  return Boolean(expiry) && expiry > Date.now();
}

function walletCardStatusCalculation(walletCardStatus: string | null) {
  const walletCardStatusLower = walletCardStatus?.toLowerCase();
  return {
    isWalletCardClaimed: walletCardStatusLower === 'claimed' || walletCardStatusLower === 'issued',
    isWalletCardBatched: walletCardStatusLower === 'batched',
    isWalletCardReturned: walletCardStatusLower === 'returned',
  };
}

function useReturnedWalletCardSubmit(): (street1: string, street2: string, suggestion: SmartySuggestion) => Promise<void> {
  const { enqueueSnackbar } = useSnackbar();
  const user = useContext(UserContext);
  const { setLoading, moveTo } = useContext(WalletCardContext);

  return useCallback(async (street1: string, street2: string, suggestion: SmartySuggestion) => {
    setLoading(true);
    try {
      const combinedStreet1 = street2 ? `${street1}\n${street2}` : street1;
      const updatedAddress = {
        ...user.address,
        street1: combinedStreet1,
        city: suggestion.city,
        stateRegion: suggestion.state,
        postalCode: suggestion.zipcode,
        country: 'United States',
        countryCode: 'US',
      };

      await UserService.updateCurrentUser({ ...user, address: updatedAddress } as any);
      await CredentialService.claimWalletCard();
      user.refetchUser();
      moveTo(2);
    } catch (error) {
      enqueueSnackbar(errorMessage(error), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, moveTo, setLoading, user]);
}

function useClaimWalletCard(): () => Promise<void> {
  const { enqueueSnackbar } = useSnackbar();
  const { refetchUser } = useContext(UserContext);
  const { setLoading, moveNext } = useContext(WalletCardContext);

  return useCallback(async () => {
    setLoading(true);
    try {
      // Claim wallet card.
      await CredentialService.claimWalletCard();
      refetchUser();
      moveNext();
    } catch (error) {
      enqueueSnackbar(errorMessage(error), { variant: 'error' });
    }
    setLoading(false);
  }, [enqueueSnackbar, moveNext, refetchUser, setLoading]);
}

interface WalletCardContext {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  closeModal: () => void;
  dismissModal: (doNotDisplay: any) => void;
  moveNext: () => void;
  moveTo: (step: number) => void;
}

const WalletCardContext = createContext<WalletCardContext>({
  setLoading: () => undefined,
  closeModal: () => undefined,
  dismissModal: (doNotDisplay) => undefined,
  moveNext: () => undefined,
  moveTo: () => undefined,
});
